import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  LinearProgress,
  Typography,
  Link,
  IconButton,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import PrimaryButton from "src/components/atoms/button/Primary-Button";
import "./upload-button.css";
import { useAuth } from "src/components/organisms/auth/AuthContext";

import { IFileInfo } from "src/types/models/business";
import { UploadButtonProps } from "./upload-button.props";
import { UploadButtonViewModel } from "./upload-button.view-model";

const UploadButton: React.FC<UploadButtonProps> = ({
  onUploadSuccess,
  disabled = false,
}) => {
  const [fileSrc, setFileSrc] = useState<IFileInfo | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadController, setUploadController] =
    useState<AbortController | null>(null);

  const { user } = useAuth();
  const viewModel = useRef<UploadButtonViewModel | null>(null);

  useEffect(() => {
    if (user && user.accessToken) {
      viewModel.current = new UploadButtonViewModel(user.accessToken);
    }
  }, [user]);

  useEffect(() => {
    // console.log("###### fileSrc-useEffect", fileSrc);
    if (fileSrc) {
      onUploadSuccess(fileSrc);
    }
  }, [fileSrc]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      console.log(selectedFile.name);
      setUploading(true);
      setUploadError(null);

      // Show preview of the selected image
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
        }
      };
      reader.readAsDataURL(selectedFile);

      // Create an AbortController to be able to cancel the upload
      const controller = new AbortController();
      setUploadController(controller);

      try {
        // Prepare the form data for upload
        // const formData = new FormData();
        // formData.append("file", selectedFile);
        // formData.append("fileName", selectedFile.name);
        // console.log("^^^^^^^^selectedFile.type", selectedFile.type);
        const uploadedFile =
          await viewModel.current?.uploadFileAsync(selectedFile);

        if (!uploadedFile) {
          setUploadError("Looks like the upload didn't go through. Try again?");
        } else {
          const fileInfo: IFileInfo = {
            key: uploadedFile?.key,
            version: uploadedFile?.version,
            fileName: selectedFile.name,
            signedUrl: uploadedFile.signedUrl,
          };
          if (uploadedFile) {
            console.log("#####uploadedFile", uploadedFile);
            setFileSrc(() => fileInfo);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          if (error.name === "AbortError") {
            console.log("Upload cancelled");
          } else {
            setUploadError(
              "Looks like the upload didn't go through. Try again?"
            );
          }
        } else {
          setUploadError("An unexpected error occurred.");
        }
      } finally {
        setUploading(false);
        setUploadController(null);
      }
    }
  };

  const handleCancelUpload = () => {
    if (uploadController) {
      uploadController.abort();
    }
  };

  const handleRemoveImage = () => {
    setFileSrc(undefined); // Reset to the default image
    setUploadError(null);
  };
  return (
    <Grid>
      <Grid item sx={{ minWidth: "150px" }}>
        <PrimaryButton
          variant="outlined"
          color="info"
          label="BROWSE"
          cssClassName="primary-button"
          disabled={uploading || disabled}
        >
          <input type="file" hidden onChange={handleFileChange} />
        </PrimaryButton>
      </Grid>
      {uploading && (
        <Grid item style={{ width: "100%", marginTop: "10px" }}>
          <LinearProgress />
          <IconButton onClick={handleCancelUpload} size="small">
            <CancelIcon />
          </IconButton>
        </Grid>
      )}
      {uploadError && (
        <Grid item>
          <Typography color="error">{uploadError}</Typography>
        </Grid>
      )}
      {!uploading && fileSrc !== undefined && (
        <Grid item>
          <Link
            component="button"
            variant="body2"
            onClick={handleRemoveImage}
            sx={{ textDecoration: "none", cursor: "pointer", color: "blue" }}
          >
            Clear
          </Link>
        </Grid>
      )}
    </Grid>
  );
};

export default UploadButton;
